<template>
  <Layout>
    <PageHeader
      :title="$t('module.userPermissionDefinition')"
      :items="breadcrumbs"
    />
    <b-alert v-if="!canRead" show variant="warning">{{
      $t("message.noPermissionMessage")
    }}</b-alert>
    <template v-else>
      <div class="card">
        <div class="card-body">
          <b-form-input
            v-model.trim="search"
            type="search"
            :placeholder="$t('label.search')"
            :aria-label="$t('label.search')"
          />
        </div>
      </div>
      <div v-if="loading" class="text-center p-5">
        <b-spinner variant="info" />
      </div>
      <b-alert v-else-if="loadError" show variant="danger"
        >{{ $t("label.permissionDefinitionLoadError") }}
        <b-button size="sm" variant="outline-danger" @click="loadDefinitions">{{
          $t("label.retryPermissionDefinitions")
        }}</b-button></b-alert
      >
      <template v-else>
        <div v-for="group in filteredGroups" :key="group.key" class="card">
          <div class="card-body">
            <b-button
              variant="link"
              class="d-flex justify-content-between align-items-center w-100 p-0 text-dark text-left text-decoration-none"
              :aria-expanded="!!expandedGroups[group.key]"
              :aria-controls="`permission-group-${group.key}`"
              @click="toggleGroup(group.key)"
            >
              <span class="h5 d-block mb-1">{{ group.name }}</span>

              <i
                class="mdi font-size-24 ml-3"
                :class="
                  expandedGroups[group.key]
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                aria-hidden="true"
              ></i>
            </b-button>
            <b-collapse
              :id="`permission-group-${group.key}`"
              :visible="!!expandedGroups[group.key]"
              class="mt-3"
            >
              <b-table
                :items="group.options"
                :fields="fields"
                primary-key="value"
                responsive
                show-empty
              >
                <template #cell(value)="{ item }"
                  ><b-badge
                    :variant="
                      item.value === 0
                        ? 'secondary'
                        : item.value === 1
                        ? 'info'
                        : 'success'
                    "
                    >{{ item.value }}</b-badge
                  ></template
                >
                <template #cell(text)="{ item }"
                  ><div class="definition-name">{{ item.text }}</div></template
                >
                <template #cell(description)="{ item }"
                  ><div class="definition-description">
                    {{ item.description || "-" }}
                  </div></template
                >
                <template #cell(actions)="{ item }"
                  ><b-button
                    v-if="canWrite"
                    variant="link"
                    class="p-0"
                    :disabled="saving || !item.uuid"
                    :title="$t('label.updateX', [])"
                    :aria-label="$t('label.updateX', [])"
                    @click="openEdit(group, item)"
                    ><i
                      class="mdi mdi-pencil-outline font-size-18"
                    ></i></b-button
                  ><b-button
                    variant="link"
                    class="p-0 ml-2"
                    :title="$t('label.permissionDefinitionViewUsers')"
                    :aria-label="$t('label.permissionDefinitionViewUsers')"
                    @click="openUsers(group, item)"
                    ><i class="mdi mdi-eye-outline font-size-18"></i></b-button
                ></template>
              </b-table>
            </b-collapse>
          </div>
        </div>
        <b-alert v-if="!filteredGroups.length" show variant="info">{{
          $t("label.permissionDefinitionEmpty")
        }}</b-alert>
      </template>
    </template>
    <b-modal
      id="permission-definition-edit"
      :title="$t('label.updateX', [$t('module.userPermissionDefinition')])"
      hide-footer
      centered
      :no-close-on-backdrop="saving"
      :no-close-on-esc="saving"
      :hide-header-close="saving"
      @hidden="editVisible = false"
    >
      <form v-if="editVisible && canWrite" @submit.prevent="saveDefinition">
        <p class="text-muted">
          {{ editGroup }} · {{ $t("label.permissionDefinitionLevel") }}
          {{ editLevel }}
        </p>
        <b-alert v-if="saveError" show variant="danger">{{
          saveError
        }}</b-alert>
        <fieldset :disabled="saving">
          <b-form-group :label="$t('label.name')"
            ><b-form-input v-model.trim="form.name" required
          /></b-form-group>
          <b-form-group :label="$t('label.description')"
            ><b-form-textarea v-model="form.description" rows="4"
          /></b-form-group>
        </fieldset>
        <div class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="outline-secondary"
            class="mr-2"
            :disabled="saving"
            @click="$bvModal.hide('permission-definition-edit')"
            >{{ $t("label.cancel") }}</b-button
          >
          <b-button
            type="submit"
            variant="info"
            :disabled="saving || !form.name.trim()"
            ><b-spinner v-if="saving" small /><span v-else>{{
              $t("label.updateX", [])
            }}</span></b-button
          >
        </div>
      </form>
    </b-modal>
    <b-modal
      id="permission-definition-users"
      :title="$t('label.permissionDefinitionViewUsers')"
      size="xl"
      hide-footer
      centered
      @hidden="usersVisible = false"
    >
      <PermissionUsers
        v-if="usersVisible && canRead"
        :key="`${usersSelection.key}-${usersSelection.level}`"
        :permission-key="usersSelection.key"
        :level="usersSelection.level"
        :permission-name="usersSelection.name"
        :level-name="usersSelection.levelName"
        :level-options="usersSelection.options"
      />
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import PermissionUsers from "./users.vue";
import i18n from "@/i18n";
import { userPermissionDefinitionService } from "@/services/api/userPermissionDefinition.service";
import { userPermissionDefinitionsModel } from "@/models/api/userPermissionDefinitions.model";
import { sessionHelper, alertHelper, sharedHelper } from "@/utils";
export default {
  name: "UserPermissionDefinitionConfiguration",
  page: { title: i18n.t("module.userPermissionDefinition") },
  components: { Layout, PageHeader, PermissionUsers },
  data() {
    return {
      groups: [],
      expandedGroups: {},
      search: "",
      loading: true,
      loadError: false,
      requestId: 0,
      saving: false,
      saveError: "",
      editVisible: false,
      usersVisible: false,
      usersSelection: {
        key: "",
        level: 0,
        name: "",
        levelName: "",
        options: [],
      },
      editUUID: "",
      editGroup: "",
      editLevel: null,
      form: { name: "", description: "" },
    };
  },
  computed: {
    permission() {
      const user = this.$store.state.api.auth.user?.user;
      const permissions = sessionHelper.getUserPermissions(
        user?.permissions,
        user?.isSuperadmin,
      );
      return Math.max(permissions.developer || 0, permissions.user_admin || 0);
    },
    canRead() {
      return this.permission >= 1;
    },
    canWrite() {
      return this.permission === 2;
    },
    breadcrumbs() {
      return [
        { text: this.$t("module.administration"), href: "/administrations" },
        { text: this.$t("module.userPermissionDefinition"), active: true },
      ];
    },
    fields() {
      return [
        { key: "value", label: this.$t("label.permissionDefinitionLevel") },
        { key: "text", label: this.$t("label.name") },
        { key: "description", label: this.$t("label.description") },
        { key: "actions", label: this.$t("label.action") },
      ];
    },
    filteredGroups() {
      const term = this.search.toLowerCase();
      return this.groups.filter((group) =>
        [
          group.key,
          group.name,
          ...group.options.flatMap((item) => [item.text, item.description]),
        ].some((value) => value.toLowerCase().includes(term)),
      );
    },
  },
  watch: {
    permission(level) {
      if (level < 2) {
        this.$bvModal.hide("permission-definition-edit");
        this.editVisible = false;
      }
      if (level < 1) {
        this.$bvModal.hide("permission-definition-users");
        this.usersVisible = false;
      }
      this.loadDefinitions();
    },
  },
  created() {
    this.loadDefinitions();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    openUsers(group, item) {
      if (this.currentPermission() < 1) return;
      this.usersSelection = {
        key: group.key,
        level: item.value,
        name: group.name,
        levelName: item.text,
        options: group.options,
      };
      this.usersVisible = true;
      this.$nextTick(() => this.$bvModal.show("permission-definition-users"));
    },
    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
    },
    currentPermission() {
      return Math.max(
        sessionHelper.getPermissionLevel("developer"),
        sessionHelper.getPermissionLevel("user_admin"),
      );
    },
    async loadDefinitions() {
      const request = ++this.requestId;
      if (this.currentPermission() < 1) {
        this.groups = [];
        this.loading = false;
        this.loadError = false;
        return;
      }
      this.loading = true;
      this.loadError = false;
      try {
        const result = await userPermissionDefinitionService.getAll();
        if (request !== this.requestId) return;
        this.groups = userPermissionDefinitionsModel(result);
      } catch (error) {
        if (request === this.requestId) this.loadError = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    openEdit(group, item) {
      if (this.saving || !item.uuid || this.currentPermission() !== 2) return;
      this.editUUID = item.uuid;
      this.editGroup = group.name;
      this.editLevel = item.value;
      this.form = { name: item.text, description: item.description };
      this.saveError = "";
      this.editVisible = true;
      this.$nextTick(() => this.$bvModal.show("permission-definition-edit"));
    },
    async saveDefinition() {
      if (
        this.saving ||
        !this.editUUID ||
        !this.form.name.trim() ||
        this.currentPermission() !== 2
      )
        return;
      this.saving = true;
      this.saveError = "";
      try {
        const result = await userPermissionDefinitionService.update(
          this.editUUID,
          this.form,
        );
        if (!result?.success)
          throw new Error(result?.message || this.$t("message.unknownError"));
        alertHelper.alertSuccess({
          message: this.$t("message.updateXSuccess", [
            this.$t("module.userPermissionDefinition"),
          ]),
        });
        this.$bvModal.hide("permission-definition-edit");
        await this.loadDefinitions();
      } catch (error) {
        this.saveError =
          sharedHelper.getErrorMessage(error) ||
          this.$t("message.unknownError");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style scoped>
.definition-name {
  min-width: 150px;
  max-width: 300px;
  overflow-wrap: anywhere;
}
.definition-description {
  min-width: 200px;
  max-width: 600px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
